import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { AdminOrderItem } from '@/services/adminService';
import { formatOrderItemReadableCode } from './utils';

interface ReuploadCommentDialogProps {
  open: boolean;
  item: AdminOrderItem | null;
  comment: string;
  isSubmitting: boolean;
  onCommentChange: (value: string) => void;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

const ReuploadCommentDialog = ({
  open,
  item,
  comment,
  isSubmitting,
  onCommentChange,
  onConfirm,
  onOpenChange,
}: ReuploadCommentDialogProps) => {
  const { t } = useTranslation();
  const customerLabel = item?.customer?.email ?? item?.customer?.name ?? t('adminOrdersWorkspace.common.unknownCustomer', 'Unknown customer');
  const itemCode = item ? formatOrderItemReadableCode(item.id) : '—';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-xl -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg focus:outline-none">
          <div className="space-y-4">
            <div className="space-y-1">
              <Dialog.Title className="text-lg font-semibold text-foreground">
                {t('adminOrdersWorkspace.reuploadDialog.title', 'Request Image Reupload')}
              </Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">
                {t(
                  'adminOrdersWorkspace.reuploadDialog.description',
                  'Write the comment that will be sent to the user for this image correction request.',
                )}
              </Dialog.Description>
            </div>

            <div className="rounded-md border border-border/70 bg-muted/30 p-3 text-sm text-muted-foreground">
              <p>{t('adminOrdersWorkspace.reuploadDialog.customer', 'Customer')}: {customerLabel}</p>
              <p>{t('adminOrdersWorkspace.reuploadDialog.order', 'Order')}: {item?.order?.displayCode ?? '—'}</p>
              <p>{t('adminOrdersWorkspace.reuploadDialog.item', 'Item')}: {itemCode}</p>
              <p>{t('adminOrdersWorkspace.reuploadDialog.file', 'File')}: {item?.uploadedFile?.filename ?? '—'}</p>
            </div>

            <div className="space-y-2">
              <label htmlFor="reupload-comment" className="text-sm font-medium text-foreground">
                {t('adminOrdersWorkspace.reuploadDialog.commentLabel', 'Comment to user')}
              </label>
              <Textarea
                id="reupload-comment"
                value={comment}
                onChange={(event) => onCommentChange(event.target.value)}
                placeholder={t(
                  'adminOrdersWorkspace.reuploadDialog.placeholder',
                  'Explain what should be corrected before the user uploads a new version.',
                )}
                className="min-h-[160px]"
                disabled={isSubmitting}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {t('adminOrdersWorkspace.reuploadDialog.cancel', 'Cancel')}
              </Button>
              <Button
                type="button"
                onClick={onConfirm}
                disabled={isSubmitting || comment.trim().length === 0}
              >
                {isSubmitting
                  ? t('adminOrdersWorkspace.reuploadDialog.sending', 'Sending...')
                  : t('adminOrdersWorkspace.reuploadDialog.submit', 'Send request')}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ReuploadCommentDialog;
